import { useCallback, useRef } from 'react';

/* eslint-disable import/order -- CI lints `-api` before `/` (correct ASCII), local eslint --fix reverses it; disable to keep CI happy. */
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
/* eslint-enable import/order */

import { useSpaces, useSpaceStoreActions } from '../../controllers/space-store/hooks';
import type { Space } from '../../controllers/space-store/types';

import { deleteSpaceFetchAction } from './useDeleteSpace.fetch';

type UseDeleteSpaceProps = {
	cloudId: string;
	product: string;
	experienceId: string;
	onSuccess?: (spaceId: string) => void;
};

/**
 * Deletes a space by id. Performs an optimistic removal from the store and
 * rolls back on failure (re-inserting the captured space and clearing its
 * tombstone so future list refetches can surface it again).
 *
 * With rovo_ai_mate_reliability_contracts enabled, request failures reject after
 * rollback so the caller can keep its error state visible. An in-flight DELETE
 * is allowed to settle: stopping the local request cannot undo a server deletion.
 */
export const useDeleteSpace = ({
	cloudId,
	product,
	experienceId,
	onSuccess,
}: UseDeleteSpaceProps): {
	deleteSpace: (spaceId: string) => Promise<void>;
} => {
	const { removeSpace, restoreSpace } = useSpaceStoreActions();
	const spaces = useSpaces();

	// Stable refs so neither `spaces` nor a churning consumer-supplied
	// `onSuccess` becomes a `useCallback` dependency that thrashes the
	// returned `deleteSpace` identity. Matches the pre-migration pattern.
	const spacesRef = useRef<Space[]>(spaces);
	spacesRef.current = spaces;
	const onSuccessRef = useRef(onSuccess);
	onSuccessRef.current = onSuccess;

	// Snapshot of pre-delete spaces keyed by spaceId. Used by `onError` to
	// roll back the optimistic removal — `useMutation`'s `onError(error,
	// payload)` exposes the original `spaceId`, so we look up the snapshot
	// and restore.
	const deletedSpacesRef = useRef<Map<string, Space>>(new Map());

	const { mutate } = useMutation<string, string>({
		throwOnError: true,
		mutationFn: async (spaceId) => {
			const deletedSpace = spacesRef.current.find((s) => s.id === spaceId);
			if (deletedSpace) {
				deletedSpacesRef.current.set(spaceId, deletedSpace);
			}
			removeSpace(spaceId);
			await deleteSpaceFetchAction({
				spaceId,
				cloudId,
				product,
				experienceId,
			});
			deletedSpacesRef.current.delete(spaceId);
			return spaceId;
		},
		onSuccess: (spaceId) => onSuccessRef.current?.(spaceId),
		onError: (_error: ApiError, spaceId) => {
			const deletedSpace = deletedSpacesRef.current.get(spaceId);
			if (deletedSpace) {
				restoreSpace(deletedSpace);
				deletedSpacesRef.current.delete(spaceId);
			}
		},
	});

	const deleteSpace = useCallback(
		async (spaceId: string): Promise<void> => {
			await mutate(spaceId);
		},
		[mutate],
	);

	return { deleteSpace };
};
